// Widget Gatekeeper - CI/CD
import { useState, useEffect } from "react";
import { GitBranch, CheckCircle2, XCircle, Clock, RefreshCw, Play, Loader2 } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";

const API = process.env.REACT_APP_BACKEND_URL;

export default function GouvGatekeeper() {
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statusRes, historyRes] = await Promise.all([
        fetch(`${API}/api/groot/gatekeeper/status`),
        fetch(`${API}/api/groot/gatekeeper/history?limit=10`)
      ]);
      
      if (statusRes.ok) {
        setStatus(await statusRes.json());
      }
      if (historyRes.ok) {
        const data = await historyRes.json();
        setHistory(data.scans || []);
      }
    } catch (err) {
      console.error("Erreur:", err);
    } finally {
      setLoading(false);
    }
  };

  const runScan = async () => {
    setScanning(true);
    try {
      await fetch(`${API}/api/groot/gatekeeper/scan?trigger=manual&run_e2e=true`, {
        method: "POST"
      });
      await loadData();
    } catch (err) {
      console.error("Erreur scan:", err);
    } finally {
      setScanning(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 text-purple-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <GitBranch className="w-5 h-5 text-purple-400" />
            Gatekeeper CI/CD
          </h2>
          <p className="text-sm text-zinc-500 mt-1">Tests E2E et validation déploiement</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={loadData} variant="outline" size="sm" className="bg-zinc-800 border-zinc-700">
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualiser
          </Button>
          <Button onClick={runScan} disabled={scanning} className="bg-purple-600 hover:bg-purple-700">
            {scanning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
            Lancer un scan
          </Button>
        </div>
      </div>

      {/* Status actuel */}
      <div className={`p-6 rounded-xl border ${
        status?.latest_scan?.can_deploy 
          ? 'bg-emerald-500/10 border-emerald-500/30'
          : 'bg-red-500/10 border-red-500/30'
      }`}>
        <div className="flex items-center gap-4">
          {status?.latest_scan?.can_deploy ? (
            <CheckCircle2 className="w-10 h-10 text-emerald-400" />
          ) : (
            <XCircle className="w-10 h-10 text-red-400" />
          )}
          <div>
            <h3 className={`text-lg font-semibold ${
              status?.latest_scan?.can_deploy ? 'text-emerald-400' : 'text-red-400'
            }`}>
              {status?.latest_scan?.can_deploy ? 'Déploiement Autorisé' : 'Déploiement Bloqué'}
            </h3>
            <p className="text-sm text-zinc-400">
              Dernier scan: {status?.latest_scan?.completed_at 
                ? new Date(status.latest_scan.completed_at).toLocaleString("fr-FR")
                : 'Jamais'}
            </p>
          </div>
        </div>
        
        {status?.latest_scan?.block_reasons?.length > 0 && (
          <div className="mt-4 pt-4 border-t border-red-500/20">
            <p className="text-sm font-medium text-red-400 mb-2">Raisons du blocage:</p>
            <ul className="space-y-1">
              {status.latest_scan.block_reasons.map((r, i) => (
                <li key={i} className="text-sm text-red-300 flex items-center gap-2">
                  <XCircle className="w-4 h-4" />
                  {r}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Historique */}
      <div>
        <h3 className="text-lg font-semibold text-white mb-4">Historique des scans</h3>
        <div className="space-y-2">
          {history.map(scan => (
            <div 
              key={scan.scan_id}
              className="flex items-center justify-between p-4 bg-zinc-800/50 rounded-lg border border-zinc-700"
            >
              <div className="flex items-center gap-4">
                {scan.can_deploy ? (
                  <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-400" />
                )}
                <div>
                  <p className="text-sm font-mono text-zinc-400">{scan.scan_id}</p>
                  <p className="text-xs text-zinc-500">
                    {scan.completed_at ? new Date(scan.completed_at).toLocaleString("fr-FR") : '-'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="secondary">{scan.trigger}</Badge>
                {scan.summary?.critical > 0 && (
                  <Badge className="bg-red-500/20 text-red-400">
                    {scan.summary.critical} critiques
                  </Badge>
                )}
              </div>
            </div>
          ))}
          {history.length === 0 && (
            <p className="text-center py-8 text-zinc-500">Aucun scan dans l'historique</p>
          )}
        </div>
      </div>
    </div>
  );
}
